import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  ORGANISATION_ROLE_LABELS,
  useOrganisation,
} from '@/contexts/OrganisationContext';
import { useOnboarding } from './OnboardingContext';
import { getGivenName } from '@/lib/displayName';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import meddLogo from '@/assets/medd-logo.png';
import { 
  Users, 
  LineChart, 
  Trophy, 
  MessageSquare, 
  ChevronRight,
  Building2,
  UserCircle,
  Sparkles,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const FEATURES = [
  {
    icon: Users,
    title: 'AI Simulations',
    description: 'Practice patient case studies, customer role plays, coaches, and examiner scenarios',
  },
  {
    icon: LineChart,
    title: 'Track Progress',
    description: 'Monitor your improvement with detailed performance analytics',
  },
  {
    icon: Trophy,
    title: 'Leaderboards',
    description: 'Compare your scores with peers and climb the rankings',
  },
  {
    icon: MessageSquare,
    title: 'Coaching',
    description: 'Get personalized feedback and coaching to enhance your skills',
  },
];

export function OnboardingModal() {
  const { user } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const {
    hasEnterpriseHierarchy,
    activeAssignment,
    activeUnit,
    effectiveRole,
    globalCompanyName,
  } = useOrganisation();
  const { showOnboarding, completeOnboarding } = useOnboarding();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  
  // Use tenant branding
  const platformName = tenant.tenant_name || 'MEDD-SIM';
  const logoUrl = !isMasterTenant && tenant.logo_url ? tenant.logo_url : meddLogo;
  const primaryColor = tenant.primary_color || '#5FB3A1';

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const handleGetStarted = () => {
    completeOnboarding();
    navigate('/simulations');
  };

  const getRoleDisplay = () => {
    if (effectiveRole) {
      return effectiveRole === 'user'
        ? 'Team Member'
        : ORGANISATION_ROLE_LABELS[effectiveRole];
    }
    if (!user?.roles?.length) return 'Team Member';
    const roleMap: Record<string, string> = {
      admin: 'System Administrator',
      company_admin: 'Company Administrator',
      sales_rep: 'Medical Sales Representative',
      user: 'Team Member',
    };
    const firstRole = user.roles.find(r => roleMap[r]);
    return firstRole ? roleMap[firstRole] : 'Team Member';
  };

  if (!showOnboarding) return null;

  return (
    <Dialog open={showOnboarding} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Welcome Onboarding</DialogTitle>
        <DialogDescription className="sr-only">Get started with the platform features and navigation</DialogDescription>
        {/* Header with gradient */}
        <div 
          className="p-6 text-white"
          style={{ background: `linear-gradient(to bottom right, ${primaryColor}, ${primaryColor}cc)` }}
        >
          <div className="mb-4 flex items-center justify-center">
            <div className="flex h-12 w-52 items-center justify-center overflow-hidden rounded-md bg-white px-3">
              <img
                src={logoUrl}
                alt={platformName}
                className={`h-full w-full object-contain ${isMasterTenant ? '' : 'scale-[1.65]'}`}
                onError={(e) => {
                  if (isMasterTenant) {
                    (e.target as HTMLImageElement).src = meddLogo;
                  } else {
                    const image = e.target as HTMLImageElement;
                    image.style.display = 'none';
                    image.nextElementSibling?.classList.remove('hidden');
                  }
                }}
              />
              <span className="hidden truncate text-sm font-semibold text-primary">{platformName}</span>
            </div>
          </div>
          
          {step === 1 && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium opacity-90">Welcome to {platformName}</span>
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Hello, {getGivenName(user?.name)}! 🎉
              </h2>
              <p className="text-sm opacity-90">
                Your professional simulation training platform
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Platform Features</h2>
              <p className="text-sm opacity-90">
                Everything you need to excel in your training
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-300" />
              <h2 className="text-2xl font-bold mb-2">You're All Set!</h2>
              <p className="text-sm opacity-90">
                Ready to start your first simulation?
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              {/* Organisation and role information */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      {hasEnterpriseHierarchy ? 'Global Company' : 'Your Company'}
                    </p>
                    <p className="font-semibold text-foreground">
                      {(hasEnterpriseHierarchy ? globalCompanyName : user?.company_name) || (
                        <span className="text-muted-foreground italic">Not assigned yet</span>
                      )}
                    </p>
                  </div>
                </div>

                {hasEnterpriseHierarchy && (activeUnit?.branch_name || activeAssignment?.branch_name) && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Branch</p>
                      <p className="font-semibold text-foreground">
                        {activeUnit?.branch_name || activeAssignment?.branch_name}
                      </p>
                    </div>
                  </div>
                )}

                {hasEnterpriseHierarchy && activeUnit && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Business Unit</p>
                      <p className="font-semibold text-foreground">{activeUnit.name}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Your Role</p>
                    <p className="font-semibold text-foreground">{getRoleDisplay()}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                {platformName} helps you practice and perfect your professional skills 
                through AI-powered simulations in a safe, immersive environment.
              </p>

              {!user?.company_name && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>Note:</strong> Your company hasn't been assigned yet. 
                    Please contact your administrator to complete your setup.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map((feature, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                >
                  <feature.icon className="h-8 w-8 text-primary mb-2" />
                  <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 text-center">
              <p className="text-muted-foreground">
                Start practicing with our AI simulations to enhance your professional skills 
                and track your progress over time.
              </p>
              
              <div className="flex flex-col gap-2">
                <Button 
                  size="lg" 
                  className="w-full"
                  onClick={handleGetStarted}
                >
                  Start Your First Simulation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSkip}
                >
                  Explore Dashboard First
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          {step < 3 && (
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Skip
              </Button>
              
              {/* Progress dots */}
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((s) => (
                  <div 
                    key={s}
                    className={`h-2 rounded-full transition-all ${
                      s === step ? 'w-6 bg-primary' : 'w-2 bg-muted'
                    }`}
                  />
                ))}
              </div>
              
              <Button size="sm" onClick={handleNext}>
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((s) => (
                  <div 
                    key={s}
                    className={`h-2 rounded-full transition-all ${
                      s === step ? 'w-6 bg-primary' : 'w-2 bg-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
